/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxCallbacks,
    IBuyboxData,
    IBuyboxResources as IBuyboxExtentionResources,
    IBuyboxFindInStoreViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps,
    IBuyboxState,
    IBuyboxViewProps
} from '@msdyn365-commerce-modules/buybox';
import {
    ArrayExtensions,
    AttributesForSelectedVariantInput,
    getAttributesForSelectedVariant,
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    getProductAvailabilitiesForSelectedVariant,
    IFullOrgUnitAvailability,
    ProductAvailabilitiesForSelectedVariantInput,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import { INodeProps, ITelemetryContent, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { getUrlSync } from '@msdyn365-commerce/core';
import {
    AsyncResult,
    AttributeValue,
    CommerceProperty,
    format,
    ItemAvailability,
    ProductBehavior,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { reaction } from 'mobx';
import * as React from 'react';
import { Availability } from '../../../actions/get-product-availabilities-for-selected-variant.action';
import IncrementalQuantity from './components/incrementalQuantity';

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getVArticleIds } from '../../../actions/get-v-articles.action';
import { WS_DELIVERY_TIME } from '../../../configuration/attributes';
import { formatPrice } from '../../../modules/search-result-container/components';
import { IBuyboxProps as IBuyboxExtentionProps, IBuyboxProps } from '../definition-extensions/buybox.ext.props.autogenerated';
import { renderAddToOrderTemplateButton } from './components/buybox-add-to-order-template';

/**
 * BuyBoxConstants enum.
 */
export enum BuyBoxConstants {
    zero = 0
}

const BUSINESS_SIGN_UP_PATH = '/business-sign-up';

/**
 * On Change function.
 * @param callbacks -Callbacks.
 * @returns Update quantity.
 */
const onChangeHandler = (callbacks: IBuyboxCallbacks) => (newValue: number): boolean => {
    if (callbacks.updateQuantity) {
        return callbacks.updateQuantity(newValue);
    }
    return true;
};

/**
 * RenderQuantity.
 * @param quantityComponent - QuantityComponent.
 * @param callbacks - Callbacks.
 * @param props - Props.
 * @param state - State.
 * @param extentionResources - ExtentionResources.
 * @param _quantityLimitsMessages - QuantityLimitsMessages.
 * @param max - Max.
 * @param telemetryContent - TelemetryContent.
 * @param unitOfMeasure - Unit Of Measure.
 * @returns -- Returns.
 */
const renderQuantity = (
    quantityComponent: IBuyboxProductQuantityViewProps,
    callbacks: IBuyboxCallbacks,
    props: IBuyboxProps<IBuyboxData>,
    state: IBuyboxState,
    extentionResources: IBuyboxExtentionResources,
    _quantityLimitsMessages: React.ReactNode,

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    min: number | undefined,
    max: number | undefined,
    applyDefaultOrderSettings?: boolean,
    telemetryContent?: ITelemetryContent,
    unitOfMeasure?: React.ReactNode,
    productBehavior?: ProductBehavior
): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, LabelContainerProps, heading } = quantityComponent;
    const { resources } = props;
    const { quantity } = state;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {unitOfMeasure}
            <IncrementalQuantity
                id='ms-buybox__product-quantity-input'
                min={min}
                max={max}
                applyDefaultOrderSettings={applyDefaultOrderSettings}
                currentCount={quantity}
                onChange={onChangeHandler(callbacks)}
                inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                minQuantityText={extentionResources.minQuantityText}
                maxQuantityText={extentionResources.maxQuantityText}
                telemetryContent={telemetryContent}
                productBehavior={productBehavior}
            />
            {/* {_quantityLimitsMessages} */}
        </Node>
    );
};

/**
 * Render key in price function.
 * @param keyInPrice - KeyInPrice.
 * @returns -- Returns.
 */
const renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

/**
 * RenderAddToCart.
 * @param addToCart - AddToCart.
 * @returns -- Returns.
 */
const renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

/**
 * RenderFindInStore.
 * @param findInStore - FindInStore.
 * @returns -- Returns.
 */
const renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, storeSelector, heading, description, errors, button, modal, productPickupOptionList } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {productPickupOptionList}
            {description}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

/**
 * RenderAddToOrderTemplate.
 * @param props - Buybox view props.
 * @param state - Buybox state.
 * @param callbacks - Buybox callbacks.
 * @returns -- Returns.
 */
const renderAddToOrderTemplate = (
    props: IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>,
    state: IBuyboxState,
    callbacks: IBuyboxCallbacks
): JSX.Element | null => {
    const product = props.data.product.result;
    if (!props.addToOrderTemplate || !product) {
        return null;
    }

    const orderTemplateButton = renderAddToOrderTemplateButton(props, state, callbacks, product);

    return (
        <Node {...props.addToOrderTemplate.ContainerProps}>
            {props.addToOrderTemplate.errorBlock}
            {orderTemplateButton}
        </Node>
    );
};

/**
 * RRenderShopSimilarItem.
 * @param shopSimilarItem - ShopSimilarItem.
 * @returns -- Returns.
 */
const renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};

/**
 * RenderSocialShare.
 * @param socialShare - Social share react node array.
 * @returns -- Returns.
 */
const _renderSocialShare = (socialShare: React.ReactNode[]): JSX.Element | undefined => {
    if (!socialShare || socialShare.length === 0) {
        return undefined;
    }

    return <>{socialShare[0]}</>;
};

/**
 * RenderConfigureDropdown.
 * @param dropdown - Buybox product configure dropdown view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {select}
            {errors}
        </Node>
    );
};

/**
 * RenderConfigure.
 * @param configure - Buybox product configure view props.
 * @returns -- Returns JSX.Element.
 */
const renderConfigure = (configure: IBuyboxProductConfigureViewProps): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    return <Node {...ContainerProps}>{dropdowns.map(renderConfigureDropdown)}</Node>;
};

function getVCheckCache(): Map<string, Promise<boolean>> {
    if (typeof window !== 'undefined') {
        const w = window as any;
        if (!w.__vCheckCache) w.__vCheckCache = new Map<string, Promise<boolean>>();
        return w.__vCheckCache;
    }
    return new Map<string, Promise<boolean>>();
}

function hasVArticle(productId: string, actionContext: any): Promise<boolean> {
    const cache = getVCheckCache();
    if (!cache.has(productId)) {
        const p = getVArticleIds([productId], actionContext)
            .then(list => list.length > 0)
            .catch(() => false);
        cache.set(productId, p);
    }
    return cache.get(productId)!;
}

/**
 * BuyboxView.
 * @param props - Props.
 * @returns -- Returns.
 */
const BuyboxView: React.FC<IBuyboxViewProps & IBuyboxExtentionProps<IBuyboxData>> = props => {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
    const {
        ModuleProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        MediaGalleryContainerProps,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        productComparisonButton,
        configure,
        findInStore,
        quantity,
        price,
        // title,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        min,
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Dependency from Buybox.tsx file
        max,
        applyDefaultOrderSettings,
        unitOfMeasure,
        callbacks,
        state,
        resources,
        quantityLimitsMessages,
        telemetryContent,
        app: { config: appConfig },
        catalogs,
        context,
        data,
        telemetry
    } = props;
    let skuText: string = props.resources.skuText;
    skuText += props.data.product.result?.ItemId;
    const preferredStoreInfo: IStoreInfo | undefined = props.data.storeSelectorStateManager.result?.preferredStore;
    const preferredStoreId = preferredStoreInfo?.StoreId ? preferredStoreInfo.StoreId : '';
    const preferredStoreName = preferredStoreInfo?.StoreName ? preferredStoreInfo.StoreName : '';
    const product = props.data.product.result;
    const productBehavior = product?.Behavior;
    const surfaceFinishingKey: string = 'SurfaceFinishing';
    const [surfaceFinishingValue, setSurfaceFinishingValue] = React.useState<string>('');
    const [surfaceFinishingDisabled, setSurfaceFinishingDisabled] = React.useState<boolean>(true);
    const enabledOnceRef = React.useRef(false);
    const productId = React.useMemo(() => (product?.ItemId ? String(product.ItemId) : ''), [product?.ItemId]);
    const [longerdeliveryMessage, setLongerDeliveryMessage] = React.useState<string>('');

    const loadAttributes = async () => {
        if (!data.product.result) {
            return;
        }

        const catalogId = Msdyn365.getCatalogId(context.request);
        validateCatalogId(catalogId);

        const actionInput = new AttributesForSelectedVariantInput(
            data.product.result.RecordId,
            +context.actionContext.requestContext.apiSettings.channelId,
            data.product.result,
            catalogId
        );

        try {
            const result = await getAttributesForSelectedVariant(actionInput, context.actionContext);

            const attributes = result as AttributeValue[];

            if (!attributes || attributes.length === 0) {
                return;
            }

            const deliveryAttribute = attributes.find(attr => attr.Name === WS_DELIVERY_TIME);

            if (deliveryAttribute?.TextValue) {
                setLongerDeliveryMessage(deliveryAttribute.TextValue);
            }
        } catch (error) {
            telemetry.error((error as Error).message);
        }
    };

    React.useEffect(() => {
        loadAttributes();
        let cancelled = false;

        if (!productId) {
            enabledOnceRef.current = false;
            setSurfaceFinishingDisabled(true);
            return () => {
                cancelled = true;
            };
        }

        hasVArticle(productId, props.context.actionContext).then(hasAny => {
            if (cancelled) return;

            if (hasAny) {
                enabledOnceRef.current = true;
                setSurfaceFinishingDisabled(false);
            } else if (!enabledOnceRef.current) {
                setSurfaceFinishingDisabled(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [productId]);

    const _updateProductExtensionPropertyText = (extensionPropertyKey: string, product?: SimpleProduct) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (product) {
            const value = event.currentTarget.value;
            setSurfaceFinishingValue(value);

            product.ExtensionProperties = product.ExtensionProperties || [];

            const existingIndex = product.ExtensionProperties.findIndex(
                (extension: CommerceProperty) => extension.Key === extensionPropertyKey
            );

            if (value && value.trim().length > 0) {
                // Check if value exists and push to ExtensionProperties
                if (existingIndex >= 0) {
                    product.ExtensionProperties[existingIndex].Value = { StringValue: value };
                } else {
                    product.ExtensionProperties.push({
                        Key: extensionPropertyKey,
                        Value: {
                            StringValue: value
                        }
                    });
                }
            } else {
                // Remove property, if value is empty
                if (existingIndex >= 0) {
                    product.ExtensionProperties.splice(existingIndex, 1);
                }
            }
        }
    };

    const renderSurfaceFinishing = () => (
        <div className='ms-buybox__surface'>
            <input
                className='ms-buybox__surface-finishing-input'
                type='text'
                aria-label={'Add surface finishing'}
                value={surfaceFinishingValue}
                disabled={surfaceFinishingDisabled}
                placeholder={resources.addSurfaceFinishingText}
                onChange={_updateProductExtensionPropertyText(surfaceFinishingKey, product)}
            />
        </div>
    );

    const [isStoreChange, setIsStoreChange] = React.useState<boolean>(false);

    /**
     * GetStockInventoryLabel.
     * @param availabilityWithHours - Stock availability with hours.
     * @returns -- Returns.
     */
    const getStockInventoryLabel = (availabilityWithHours: IFullOrgUnitAvailability | undefined): string | undefined => {
        if (availabilityWithHours && ArrayExtensions.hasElements(availabilityWithHours.ProductInventoryInformation)) {
            for (const productInventoryInformation of availabilityWithHours.ProductInventoryInformation) {
                if (
                    availabilityWithHours.OrgUnitAvailability?.OrgUnitLocation?.InventoryLocationId ===
                    productInventoryInformation.InventLocationId
                ) {
                    return productInventoryInformation.StockLevelLabel;
                }
            }
        }

        return undefined;
    };

    const getInventory = React.useCallback(async () => {
        const input = new GetFullAvailableInventoryNearbyInput(
            props.data.product.result?.RecordId,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            BuyBoxConstants.zero,
            true
        );

        /**
         * IsProductInStock.
         * @param itemAvailabilities - Item availability array.
         * @returns -- Returns.
         */
        const isProductInStock = (itemAvailabilities: ItemAvailability[] | undefined): boolean => {
            if (!appConfig.enableStockCheck) {
                return true;
            }

            if (ArrayExtensions.hasElements(itemAvailabilities)) {
                return itemAvailabilities[BuyBoxConstants.zero].AvailableQuantity! > BuyBoxConstants.zero;
            }

            return false;
        };

        await getFullAvailableInventoryNearby(input, props.context.actionContext)
            .catch((error: Error) => {
                props.telemetry.error(error.message);
            })
            .then(response => {
                if (response && ArrayExtensions.hasElements(response)) {
                    const isPreferredStore = (value: IFullOrgUnitAvailability) =>
                        value.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === preferredStoreId;
                    const filteredFullOrgUnitAvailability = response.filter(isPreferredStore);
                    const preferredStoreAvailability = ArrayExtensions.hasElements(filteredFullOrgUnitAvailability)
                        ? filteredFullOrgUnitAvailability[BuyBoxConstants.zero]
                        : undefined;
                    const stockStatusLabel: string | undefined = getStockInventoryLabel(preferredStoreAvailability);
                    const isInStock = isProductInStock(preferredStoreAvailability?.OrgUnitAvailability?.ItemAvailabilities);
                    const resourceLabel = isInStock ? props.resources.inStockText : props.resources.outOfStockText;
                    const stockText: string | undefined = stockStatusLabel ? stockStatusLabel : resourceLabel;
                    const preferredStoreAvailabilityText = `${format(
                        props.resources.availabilityAtPreferredStoreText,
                        preferredStoreName
                    )} ${stockText}`;
                    setAvailability(preferredStoreAvailabilityText);
                }
            });

        setIsStoreChange(false);
    }, [
        props.data.product.result?.RecordId,
        appConfig.enableStockCheck,
        preferredStoreId,
        preferredStoreName,
        props.context.actionContext,
        props.resources.availabilityAtPreferredStoreText,
        props.resources.inStockText,
        props.resources.outOfStockText,
        props.telemetry
    ]);

    React.useEffect(() => {
        if (props.config.displayStockAvailability) {
            getInventory().catch((error: Error) => {
                props.telemetry.error(error.message);
            });
        }
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability]);

    reaction(
        () => props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
        () => {
            setIsStoreChange(true);
        }
    );

    /**
     * RenderAvailabiltyAtPreferredStore.
     * @returns -- Returns.
     */
    const renderAvailabilityAtPreferredStore = (): JSX.Element | undefined => {
        const hasProductDimensions = ArrayExtensions.hasElements(props.data.product.result?.Dimensions);
        if (hasProductDimensions && props.data.product.result?.MasterProductId === undefined) {
            return undefined;
        }
        const containerProps: INodeProps = {
            className: 'ms-buybox__preferred-store-availability',
            tag: 'span'
        };

        return <Node {...containerProps}>{availability}</Node>;
    };

    const getAvailableQuantity = async (product: any, context: any): Promise<AsyncResult<any> | null> => {
        try {
            const availability = await getProductAvailabilitiesForSelectedVariant(
                new ProductAvailabilitiesForSelectedVariantInput(
                    Number(product.ProductNumber),
                    Number(context.actionContext.requestContext.channel.RecordId),
                    product
                ),
                context.actionContext
            );
            return availability;
        } catch (error) {
            return null;
        }
    };

    React.useEffect(() => {
        const minQuantity = productBehavior?.MinimumQuantity ?? 0;
        const multipleOfQuantity = productBehavior?.MultipleOfQuantity ?? 0;
        const quantityToSet = minQuantity > 0 ? minQuantity : multipleOfQuantity > 0 ? multipleOfQuantity : 1;
        callbacks.updateQuantity(Number(quantityToSet));

        const fetchAvailabilities = async () => {
            if (product) {
                const availability = await getAvailableQuantity(product, props.context);
                setPhysicalAvailability(availability?.PhysicalAvailableInventoryLevelLabel);
                setIsLoadingAvailability(false);
            }
        };

        fetchAvailabilities();

        getInventory().catch((error: Error) => {
            props.telemetry.error(error.message);
        });
    }, [getInventory, isStoreChange, props.telemetry, props.config.displayStockAvailability, props.data.product]);

    const [availability, setAvailability] = React.useState<string>('');
    const [isLoadingAvailability, setIsLoadingAvailability] = React.useState<boolean>(true);
    const [physicalAvailability, setPhysicalAvailability] = React.useState<string>('');

    const renderAvailability = (): JSX.Element | undefined => {
        const { product } = props.data;
        const hasProductDimensions = ArrayExtensions.hasElements(product.result?.Dimensions);

        if (hasProductDimensions && !product.result?.MasterProductId) {
            return undefined;
        }

        const isAvailable = physicalAvailability === Availability.available;
        const indicator = isAvailable ? Availability.availableClass : Availability.outOfStockClass;
        const availabilityLabel = isAvailable
            ? props.resources.availabilityIndicatorAvailable
            : props.resources.availabilityIndicatorOutOfStock;

        const containerProps: INodeProps = {
            className: `ms-buybox__availability ms-buybox__availability-${indicator}`,
            tag: 'span',
            children: (
                <span className='ms-buybox__availability-label'>
                    {availabilityLabel}
                    <span className='ms-buybox__availability-badge'></span>
                </span>
            )
        };

        return (
            <div className='msc-buybox__availability-section'>
                <Node {...containerProps}></Node>
                {longerdeliveryMessage && <div className='ms-buybox__longer-delivery-message'>{longerdeliveryMessage}</div>}
            </div>
        );
    };

    const discountLines = React.isValidElement(price) ? price.props?.data?.price?.DiscountLines || [] : [];
    const totalDiscountPercentage = discountLines.reduce((sum: number, line: { Percentage?: number }) => sum + (line.Percentage ?? 0), 0);
    const isAuthenticated = props.context.request.user.isAuthenticated;
    const customerContextualPrice = React.isValidElement(price) ? price?.props?.data?.price?.CustomerContextualPrice : 0;
    const currencyCode = React.isValidElement(price) ? price?.props?.data?.price?.CurrencyCode : '';
    const adjustedPrice = React.isValidElement(price) ? price?.props?.data?.price?.AdjustedPrice : 0;
    const locale = ((context.cultureFormatter as unknown) as { locale?: string })?.locale;

    return (
        <Module {...ModuleProps}>
            <div className='ms-buybox__left-column'>
                <Node {...ProductInfoContainerProps}>
                    {catalogs && catalogs[0].Name}
                    {/* {title} */}
                    <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
                    <div className='ms-buybox__product-head'>
                        <Node className='msc-buybox__description-section'>
                            {title}
                            {!isLoadingAvailability && renderAvailability()}
                            <Node className='msc-buybox__price-section'>
                                <div className='msc-buybox__price-section-text'>{isAuthenticated ? props.resources.priceText : null}</div>
                                {isAuthenticated ? (
                                    customerContextualPrice !== adjustedPrice ? (
                                        <>
                                            <span className='msc-price__strikethrough'>
                                                {formatPrice(adjustedPrice, currencyCode, locale, context.telemetry)}
                                            </span>{' '}
                                            <span className='msc-price__actual '>
                                                {formatPrice(customerContextualPrice, currencyCode, locale, context.telemetry)}
                                            </span>
                                        </>
                                    ) : (
                                        <span className='msc-price__actual'>
                                            {formatPrice(adjustedPrice, currencyCode, locale, context.telemetry)}
                                        </span>
                                    )
                                ) : (
                                    <div className='msc-buybox__auth-links'>
                                        <a
                                            href={getUrlSync(BUSINESS_SIGN_UP_PATH, context.actionContext) ?? ''}
                                            className='msc-buybox__register-link'
                                        >
                                            {props.resources.buyboxRegisterHereText}
                                        </a>
                                        <a href={context.request.user.signInUrl} className='msc-buybox__login-link'>
                                            {props.resources.buyboxLoginHereText}
                                        </a>
                                    </div>
                                )}
                            </Node>
                            {totalDiscountPercentage > 0 && (
                                <span className='msc-buybox__discount-percentage'>
                                    {props.resources.discountStringText} {totalDiscountPercentage}%
                                </span>
                            )}
                        </Node>
                        {props.config.enableRatingsSection && (
                            <Node className='msc-buybox__ratings-section'>
                                <div className='msc-buybox__ratings-section-sku-text'>{skuText}</div>
                                {rating}
                            </Node>
                        )}
                        {configure && renderConfigure(configure)}
                        {keyInPrice && renderKeyInPrice(keyInPrice)}
                        {!surfaceFinishingDisabled && renderSurfaceFinishing()}
                        <Node className='msc-buybox__purchase-section'>
                            <Node className='msc-buybox__bulk-purchase-section'>
                                <div className='msc-buybox__bulk-purchase-button-text'>
                                    {quantity &&
                                        renderQuantity(
                                            quantity,
                                            callbacks,
                                            props,
                                            state,
                                            resources,
                                            quantityLimitsMessages,
                                            min,
                                            max,
                                            applyDefaultOrderSettings,
                                            telemetryContent,
                                            unitOfMeasure,
                                            productBehavior
                                        )}
                                    {props.bulkPurchaseLink}
                                </div>
                            </Node>
                            {props.config.displayStockAvailability && preferredStoreId && renderAvailabilityAtPreferredStore()}
                            {inventoryLabel}
                            {isAuthenticated && renderAddToCart(addToCart)}
                        </Node>
                        <div className='ms-buybox__buttonBar'>
                            {findInStore && renderFindInStore(findInStore)}
                            {productComparisonButton}
                            {props.config.enableAddToOrder && addToOrderTemplate && renderAddToOrderTemplate(props, state, callbacks)}
                            {findInStore?.productPickupOptionList ? (
                                <div className='msc-buybox__pickup-options'>{findInStore.productPickupOptionList}</div>
                            ) : null}
                            {_renderSocialShare(props.slots && props.slots.socialShare)}
                            {props.config.enableDescriptionSection && (
                                <div className='msc-buybox__description-text'>{props.resources.shopText}</div>
                            )}
                            <Node className='msc-buybox__shop-description'>
                                {shopSimilarLook && renderShopSimilarItem(shopSimilarLook)}
                                {shopSimilarDescription && renderShopSimilarItem(shopSimilarDescription)}
                            </Node>
                        </div>
                    </div>
                </Node>
                {ArrayExtensions.hasElements(props.slots.productDocuments) ? (
                    <Node className='msc-buybox__product-documents'>{props.slots.productDocuments[0]}</Node>
                ) : null}
            </div>
            {ArrayExtensions.hasElements(props.slots.productSpecification) ? (
                <Node className='msc-buybox__product-specification'>{props.slots.productSpecification[0]}</Node>
            ) : null}
        </Module>
    );
};

export default BuyboxView;
